import React, { useCallback, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
// Option interface defined locally to avoid circular import
interface Option {
  isSelected?: any;
  name: string;
  value: string | number;
  imageUrl?: string;
  itemCode?: any;
}
import { useParams } from 'next/navigation';
import { classnames } from '@/helpers/utils/classnames';
import { getSkuFromUrlOrSlug, isPdpUrl } from '@/helpers/utils/pdpUrlHelper';

export interface Props {
  error?: boolean;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  options?: Option[];
  defaultValue?: Option;
  onChange?: (option: Option) => void;
  selectButtonClassName?: string;
  disableFirstOption?: boolean;
  clearDropdown?: boolean;
  optionsClassName?: any;
  disabled?: boolean;
}

const Select: React.FC<Props> = ({
  error,
  onChange,
  defaultValue,
  label,
  required,
  labelClassName = '',
  selectButtonClassName,
  disableFirstOption = false,
  clearDropdown = false,
  options = [],
  optionsClassName,
  disabled,
}) => {
  const [selected, setSelected] = useState<Option>(defaultValue || options[0] || null);

  const { slug } = useParams();
  const slugString = Array.isArray(slug) ? slug.join('/') : slug || '';
  const [valueDropDown, setValueDropDown] = useState<Option>();
  const isPdp = isPdpUrl(slugString as string);
  
  useEffect(() => {
    if (typeof slugString === 'string') {
      const variantId = getSkuFromUrlOrSlug(slugString);
      const dropDownSelectedValue = options.find((option) => option.value === variantId);
      if (dropDownSelectedValue) {
        setValueDropDown(dropDownSelectedValue);
        setSelected(dropDownSelectedValue);
      } else {
        setValueDropDown(defaultValue);
        setSelected(defaultValue || options[0] || null);
      }
    }
  }, [slugString, valueDropDown]);

  useEffect(() => {
    if (clearDropdown) {
      setSelected(defaultValue || options[0]);
    }
  }, [clearDropdown, defaultValue, options]);

  const handleChange = useCallback(
    (option: Option) => {
      setSelected(option);
      onChange?.(option);
    },
    [onChange],
  );

  
  useEffect(() => {
    const removeScrollLock = () => {
      const htmlElement = document.documentElement;
      if (htmlElement.style.overflow === 'hidden') {
        htmlElement.style.overflow = '';
        htmlElement.style.paddingRight = '';
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;
          if (target.tagName === 'HTML' && target.style.overflow === 'hidden') {
            target.style.overflow = '';
            target.style.paddingRight = '';
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => {
      observer.disconnect();
      removeScrollLock();
    };
  }, []);

  const buttonClassNames = useCallback(
    (open?: boolean) => {
      return `${error ? '!border-red-500' : 'border-black-500'}
        ${
          disabled ? 'cursor-not-allowed' : ' cursor-default cursor-pointer'
        }  relative flex  items-center pl-2.5  text-left  font-normal text-[#333] border-[#333]
       ${
         isPdp
           ? 'text-base h-11 bg-white py-4.5 pl-4 pr-8 focus:border-gray-500 active:border-gray-500'
           : ''
       }
        ${selectButtonClassName}`;
    },
    [selectButtonClassName, error],
  );

  return (
    <>
      <Listbox disabled={disabled} value={selected} onChange={handleChange}>
        {({ open }) => (
          <div className="relative w-full">
            {label && (
              <div className="mb-8">
                <span className={`${labelClassName} text-16 font-medium text-secondary-black`}>
                  {/* {required ? `${label} *` : label} */}
                  {label}
                  <span className="!text-red-500">{required && ' *'}</span>
                </span>
              </div>
            )}
            <Listbox.Button className={buttonClassNames(open)}>
              {selected?.imageUrl && (
                <img src={selected.imageUrl} alt={selected.name} className="mr-4  h-8  w-8 rounded-full" />
              )}
              {/* <span className={`${id && !id.includes('-') ? "text-14 lg:text-10 xl:text-10 2xl:text-14 font-semibold leading-[1rem] text-[#333]" : ""}`}>
                {selected?.name}
              </span> */}
              {selected?.itemCode?.endsWith('AF') || selected?.itemCode?.endsWith('LF') ? (
                <div className="flex flex-col">
                  <div
                    className={`${
                      isPdp
                        ? 'text-sm font-semibold text-[#333] lg:text-xs xl:text-xs 2xl:text-sm'
                        : ''
                    }`}
                  >
                    {selected?.name}
                    {/* {required ? `${selected?.name} *` : `${selected?.name}`} */}
                  </div>

                  <div className="ml-3 text-12 font-semibold lg:mr-5 lg:text-10 xl:text-10 2xl:text-12">
                    (Lead Free)
                  </div>
                </div>
              ) : (
                <div className="w-41.25 xl:w-full">
                  <span
                    className={`${
                      isPdp
                        ? 'md:line-clamp-1 lg:line-clamp-1   pr-8 text-sm font-semibold leading-4 text-[#333] lg:text-xs xl:text-xs 2xl:text-sm'
                        : ''
                    }`}
                  >
                    {selected?.name}
                    {/* <span className="!text-red-500">{required && ' *'}</span> */}
                  </span>
                </div>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                <ChevronDownIcon
                  className={`h-5 w-5 text-secondary-black ${open ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform origin-top scale-y-0"
              enterTo="transform origin-top scale-y-100"
              leave="transition ease-in duration-100"
              leaveFrom="transform origin-top scale-y-150"
              leaveTo="transform origin-top scale-y-0"
            >
              <Listbox.Options
                className={(active) =>
                  classnames(
                    'opacity-1 absolute top-17.5 !z-[40] max-h-[200px] w-full overflow-auto rounded-b-sm bg-white md:top-17.5 xl:top-17.5 2xl:top-17.5',
                    {
                      'border-x-black-500 border-b-black-500': active,
                      'border-black-400': !active,
                      'lg:!w-48 xl:mt-2.5 xl:!w-56 3xl:!w-76': isPdp,
                    },
                    optionsClassName || 'border-dim-gray',
                  )
                }
              >
                {options.map((option, index) => (
                  <Listbox.Option
                    key={option.value}
                    className={({ active }) =>
                      `${
                        disableFirstOption && index === 0 && 'hidden'
                      }  relative flex cursor-pointer select-none items-center py-[16px] pl-[16px] pr-[4px] text-[14px] hover:bg-neutral-200 ${
                        active ? 'bg-neutral-200' : ''
                      }`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        {option.imageUrl && (
                          <img
                            src={option.imageUrl}
                            alt={option.name}
                            className="mr-4 h-8 w-8 rounded-full lg:mr-1.25 xl:mr-4 2xl:mr-4"
                          />
                        )}

                        {option.itemCode?.endsWith('AF') || option.itemCode?.endsWith('LF') ? (
                          <div className="flex flex-col">
                            <div
                              className={`${
                                isPdp
                                  ? 'text-sm font-semibold leading-4 text-[#333] lg:text-xs xl:text-xs 2xl:text-sm'
                                  : ''
                              }`}
                            >
                              {option.name}
                            </div>
                            <div className="text-12 font-semibold lg:mr-5 lg:text-10 xl:text-10 2xl:text-12">
                              (Lead Free)
                            </div>
                          </div>
                        ) : (
                          <span
                            className={`${
                              isPdp
                                ? 'text-sm font-semibold leading-4 text-[#333] lg:text-xs xl:text-xs 2xl:text-sm'
                                : ''
                            }`}
                          >
                            {option.name}
                          </span>
                        )}
                        {option.isSelected === true && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="ml-2.5 lg:ml-0.5 xl:ml-2.5 2xl:ml-2.5"
                            x="0px"
                            y="0px"
                            width="20"
                            height="20"
                            viewBox="0 0 50 50"
                          >
                            <path d="M43.171,10.925L24.085,33.446l-9.667-9.015l1.363-1.463l8.134,7.585L41.861,9.378C37.657,4.844,31.656,2,25,2 C12.317,2,2,12.317,2,25s10.317,23,23,23s23-10.317,23-23C48,19.701,46.194,14.818,43.171,10.925z"></path>
                          </svg>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </>
  );
};

export default Select;
